import { Injectable } from '@angular/core'
import { BehaviorSubject, Observable, Subject } from 'rxjs'
import { tap } from 'rxjs/operators'
import { GuestAnswerPaginatedListResponse } from 'src/app/interfaces/answer'
import {
  CalculatedResultResponse,
  IdealTypeGuestQuizReportDetailResponse,
  IdealTypeQuizReportDetail,
  IdealTypeQuizReportDetailResponse,
  IdealTypeRankPaginatedListResponse,
  MBTI,
  MbtiBestFriendMatchCheckResponse,
  MbtiBestFriendMatchDetailResponse,
  MbtiBestFriendMatchPaginatedListResponse,
  MbtiBestFriendQuizReportDetailResponse,
  MbtiGuestQuizReportDetail,
  MbtiGuestQuizReportDetailResponse,
  MbtiHostInfoResponse,
  MbtiIdealTypeMatchCheckResponse,
  MbtiIdealTypeMatchDetailResponse,
  MbtiIdealTypeMatchPaginatedListResponse,
  MbtiIdealTypeQuizReportDetail,
  MbtiIdealTypeQuizReportDetailResponse,
  MbtiQuizReportDetail,
  MyMbtiGuestQuizReportPaginatedListResponse,
  MyMbtiHostQuizReportDetail,
  MyMbtiHostQuizReportDetailResponse,
  MyMbtiHostQuizReportPaginatedListResponse,
  TypeSummaryResponse,
} from 'src/app/interfaces/mbti'
import { MbtiPreQuestionListResponse } from 'src/app/interfaces/mbti-question'
import { GuestQuizReportData, HostInfoResponse, QuizReportPostData, QuizReportUrlType } from 'src/app/interfaces/quiz'
import { CustomHttpClient } from 'src/app/services/http/http.service'
import { LogService } from 'src/app/services/log/log.service'
import { QuizService } from 'src/app/services/quiz/quiz.service'
import { UserService } from 'src/app/services/user/user.service'
import { environment } from 'src/environments/environment'
import { QUESTION_STORE_NAME, QuestionStore } from '../database/database.service'
import { RouterService } from '../router/router.service'
import { SnackbarService } from '../snackbar/snackbar.service'
import { customLocalStorage } from './../../../utils/utils'
import { Question, QuestionAnswer } from './../../interfaces/question'
import { DataBaseService, QUESTION_KEYS } from './../database/database.service'

interface CreatingQuizReportAnswerData {
  preQuestionList: Question[]
  mbtiQuestionList: Question[]
  preQuestionAnswerList: QuestionAnswer[]
  mbtiQuestionAnswerList: QuestionAnswer[]
  hostFirstName?: string
  firstName?: string
  lastName?: string
  hideProfile?: boolean
}

const quizApiUrl = `${environment.apiUrl}/v2/question/report`
const mbtiScores = [-3, -2, -1, 1, 2, 3]
const GuestQuestionDeleteReservationKey = 'mbti_guest_question_delete_reservation'
const IdealQuizTempDataKey = 'ideal_quiz_temp_data'
const IdealMatchTempDataKey = 'ideal_match_temp_data'

@Injectable({
  providedIn: 'root',
})
export class MbtiService {
  constructor(
    private http: CustomHttpClient,
    private userService: UserService,
    private routerService: RouterService,
    private snackbarService: SnackbarService,
    private db: DataBaseService,
    private quizService: QuizService,
    private logService: LogService
  ) {}

  private userMbti: MBTI | null | undefined
  myMbti$ = new BehaviorSubject<MBTI | null | undefined>(undefined)

  // 현재 생성중인 무료 성격유형 검사 답변 상태
  private _creatingTypeTestAnswerData: CreatingQuizReportAnswerData = {
    preQuestionList: [],
    mbtiQuestionList: [],
    preQuestionAnswerList: [],
    mbtiQuestionAnswerList: [],
  }
  // 현재 생성중인 mbti quizreport 답변 상태
  private _creatingQuizReportAnswerData: CreatingQuizReportAnswerData = {
    preQuestionList: [],
    mbtiQuestionList: [],
    preQuestionAnswerList: [],
    mbtiQuestionAnswerList: [],
  }
  // 현재 접근중인 퀴즈리포트 상세 데이터.
  private _currentHostQuizReportData: MyMbtiHostQuizReportDetail | undefined
  private _currentGuestQuizReportData: MyMbtiHostQuizReportDetail | undefined
  private _hostQuizReportCreateSubject = new Subject<MbtiQuizReportDetail>()
  private _guestQuizReportCreateSubject = new Subject<MbtiQuizReportDetail>()

  get myMbti(): MBTI | null | undefined {
    return this.userMbti
  }

  set myMbti(data: MBTI | null | undefined) {
    this.userMbti = data
    this.myMbti$.next(data)
  }

  get currentHostQuizReportData(): MyMbtiHostQuizReportDetail | undefined {
    return this._currentHostQuizReportData
  }

  set currentHostQuizReportData(data: MyMbtiHostQuizReportDetail | undefined) {
    this._currentHostQuizReportData = data
  }

  get currentGuestQuizReportData(): MyMbtiHostQuizReportDetail | undefined {
    return this._currentGuestQuizReportData
  }

  set currentGuestQuizReportData(data: MyMbtiHostQuizReportDetail | undefined) {
    this._currentGuestQuizReportData = data
  }

  get creatingQuizReportAnswerData(): CreatingQuizReportAnswerData {
    return { ...this._creatingQuizReportAnswerData }
  }

  set creatingQuizReportAnswerData(data: Partial<CreatingQuizReportAnswerData>) {
    if (data.preQuestionList !== undefined) {
      this._creatingQuizReportAnswerData.preQuestionList = [...data.preQuestionList]
    }
    if (data.mbtiQuestionList !== undefined) {
      this._creatingQuizReportAnswerData.mbtiQuestionList = [...data.mbtiQuestionList]
    }
    if (data.preQuestionAnswerList !== undefined) {
      this._creatingQuizReportAnswerData.preQuestionAnswerList = [...data.preQuestionAnswerList]
    }
    if (data.mbtiQuestionAnswerList !== undefined) {
      this._creatingQuizReportAnswerData.mbtiQuestionAnswerList = [...data.mbtiQuestionAnswerList]
    }
    if (data.hostFirstName !== undefined) {
      this._creatingQuizReportAnswerData.hostFirstName = data.hostFirstName
    }
    if (!this.quizService.isNameEmpty(data.firstName)) {
      this._creatingQuizReportAnswerData.firstName = data.firstName
    }
    if (!this.quizService.isNameEmpty(data.lastName)) {
      this._creatingQuizReportAnswerData.lastName = data.lastName
    }
    if (data.hideProfile !== undefined) {
      this._creatingQuizReportAnswerData.hideProfile = data.hideProfile
    }
  }

  get creatingTypeTestAnswerData(): CreatingQuizReportAnswerData {
    return { ...this._creatingTypeTestAnswerData }
  }

  set creatingTypeTestAnswerData(data: Partial<CreatingQuizReportAnswerData>) {
    if (data.mbtiQuestionList !== undefined) {
      this._creatingTypeTestAnswerData.mbtiQuestionList = [...data.mbtiQuestionList]
    }
    if (data.mbtiQuestionAnswerList !== undefined) {
      const newDataQuestionIdList = data.mbtiQuestionAnswerList.map((item) => item.questionAnswerSet[0].question.id)
      // 중복 제거
      this._creatingTypeTestAnswerData.mbtiQuestionAnswerList =
        this._creatingTypeTestAnswerData.mbtiQuestionAnswerList.filter(
          (item) => !newDataQuestionIdList.includes(item.questionAnswerSet[0].question.id)
        )
      // 새로운 답변 데이터 추가
      this._creatingTypeTestAnswerData.mbtiQuestionAnswerList = [
        ...this._creatingTypeTestAnswerData.mbtiQuestionAnswerList,
        ...data.mbtiQuestionAnswerList,
      ]
    }
  }

  // 이상형 퀴즈 host 생성 데이터
  get idealQuizTempData(): { body: QuizReportPostData; result: IdealTypeQuizReportDetail } | undefined {
    const res = localStorage.getItem(IdealQuizTempDataKey)
    if (res) {
      return JSON.parse(res)
    }
    return
  }
  // 이상형 궁합 host 생성 데이터
  get idealMatchTempData(): { body: QuizReportPostData; result: MbtiIdealTypeQuizReportDetail } | undefined {
    const res = localStorage.getItem(IdealMatchTempDataKey)
    if (res) {
      return JSON.parse(res)
    }
    return
  }

  set idealQuizTempData(data: { body: QuizReportPostData; result: IdealTypeQuizReportDetail } | undefined) {
    localStorage.setItem(IdealQuizTempDataKey, JSON.stringify(data))
  }

  set idealMatchTempData(data: { body: QuizReportPostData; result: MbtiIdealTypeQuizReportDetail } | undefined) {
    localStorage.setItem(IdealMatchTempDataKey, JSON.stringify(data))
  }

  clearCreatingQuizReportAnswerData(): void {
    this._creatingQuizReportAnswerData = {
      preQuestionList: [],
      mbtiQuestionList: [],
      preQuestionAnswerList: [],
      mbtiQuestionAnswerList: [],
    }
  }

  clearCreatingTypeTestAnswerData(): void {
    this._creatingTypeTestAnswerData = {
      preQuestionList: [],
      mbtiQuestionList: [],
      preQuestionAnswerList: [],
      mbtiQuestionAnswerList: [],
    }
  }

  getGuestQuizReportCreateSubject(): Observable<MbtiQuizReportDetail> {
    return this._guestQuizReportCreateSubject.asObservable()
  }

  hostQuizReportCreated(result: MyMbtiHostQuizReportDetail): void {
    return this._hostQuizReportCreateSubject.next(result)
  }

  guestQuizReportCreated(result: MbtiGuestQuizReportDetail): void {
    return this._guestQuizReportCreateSubject.next(result)
  }

  /**
   * 게스트 질문 응답 내역 지우기 예약.
   * 혹시나 응답에 오류가 생길 경우 다시 바로 응답 할 수 있도록 응답 내용 60분간 저장.
   */
  reserveDeleteGuestAnsweredQuestion(
    preQuestionAnswers: QuestionAnswer[],
    mbtiQuestionAnswers: QuestionAnswer[],
    type: 'host' | 'guest',
    hostQuizReport: number
  ): void {
    const reserveList = JSON.parse(customLocalStorage.getItem(GuestQuestionDeleteReservationKey) ?? '[]')
    customLocalStorage.setItem(
      GuestQuestionDeleteReservationKey,
      JSON.stringify([
        ...reserveList,
        {
          hostQuizReport,
          preQuestionAnswers,
          mbtiQuestionAnswers,
          type,
          createdAt: new Date().getTime(),
        },
      ])
    )
  }

  /** 60분 지난 기록은 지워주기 */
  deleteReservedGuestAnsweredQuestion(): void {
    const reserveList = JSON.parse(customLocalStorage.getItem(GuestQuestionDeleteReservationKey) ?? '[]')
    const newReserveList: unknown[] = []
    const currentTime = new Date().getTime()
    reserveList.forEach((item) => {
      const createdTime = new Date(item.createdAt).getTime()
      const diffMinutes = Math.floor((currentTime - createdTime) / (1000 * 60))
      if (diffMinutes > 60) {
        const { preQuestionAnswers, mbtiQuestionAnswers, type, hostQuizReport } = item
        this.deleteAnsweredQuestion(preQuestionAnswers, mbtiQuestionAnswers, type, hostQuizReport)
      } else {
        newReserveList.push(item)
      }
    })
    customLocalStorage.setItem(GuestQuestionDeleteReservationKey, JSON.stringify(newReserveList))
  }

  deleteAnsweredQuestion(
    preQuestionAnswers: QuestionAnswer[],
    mbtiQuestionAnswers: QuestionAnswer[],
    type: 'host' | 'guest',
    hostQuizReport?: number
  ): void {
    this.db.removeItem(
      QUESTION_STORE_NAME,
      this.db.mbtiQuestionStoreAdaptor(preQuestionAnswers, type, 'pre-question', hostQuizReport),
      QUESTION_KEYS
    )
    this.db.removeItem(
      QUESTION_STORE_NAME,
      this.db.mbtiQuestionStoreAdaptor(mbtiQuestionAnswers, type, 'mbti', hostQuizReport),
      QUESTION_KEYS
    )
  }

  saveAnsweredPreQuestion(answers: QuestionAnswer[], type: 'host' | 'guest', hostQuizReport?: number): void {
    const tobeAddedQuestions = answers.filter((question) => question.value !== undefined)
    this.db.setItem(
      QUESTION_STORE_NAME,
      this.db.mbtiQuestionStoreAdaptor(tobeAddedQuestions, type, 'pre-question', hostQuizReport),
      QUESTION_KEYS
    )
  }

  saveAnsweredMbtiQuestion(answers: QuestionAnswer[], type: 'host' | 'guest', hostQuizReport?: number): void {
    const tobeAddedQuestions = answers.filter((answer) => answer.value !== undefined)
    this.db.setItem(
      QUESTION_STORE_NAME,
      this.db.mbtiQuestionStoreAdaptor(tobeAddedQuestions, type, 'mbti', hostQuizReport),
      QUESTION_KEYS
    )
  }

  getAnsweredPreQuestions(type: 'host' | 'guest', hostQuizReport?: number): { id: number; answer: string | number }[] {
    try {
      const questions: QuestionStore[] = this.db.getAllItems(QUESTION_STORE_NAME) ?? []
      const result: QuestionStore[] = questions.filter((question) => {
        return (
          question.quizType === type &&
          question.type === 'pre-question' &&
          (type === 'host' || (type === 'guest' && question.hostQuizReport === hostQuizReport))
        )
      })
      return result.map((data: QuestionStore) => {
        return { id: data.questionId, answer: data.answer }
      })
    } catch (error) {
      console.error(error)
      return []
    }
  }

  getAnsweredMbtiQuestions(type: 'host' | 'guest', hostQuizReport?: number): { id: number; answer: string | number }[] {
    try {
      const questions: QuestionStore[] = this.db.getAllItems(QUESTION_STORE_NAME) ?? []
      const result: QuestionStore[] = questions.filter((question) => {
        return (
          question.quizType === type &&
          question.type === 'mbti' &&
          (type === 'host' || (type === 'guest' && question.hostQuizReport === hostQuizReport))
        )
      })
      return result.map((data: QuestionStore) => {
        return { id: data.questionId, answer: data.answer }
      })
    } catch (error) {
      console.error(error)
      return []
    }
  }

  // 퀴즈 생성 - 호스트 사전 질문 리스트
  getHostPreQuestionList(): Observable<MbtiPreQuestionListResponse> {
    return this.http.get<MbtiPreQuestionListResponse>(`${environment.apiUrl}/v1/pre-question/host`)
  }

  // 호스트 응답 결과 저장
  // 저장시, 가장 최근에 답변했던 quizReportId 제거. (퀴즈 생성시 참고했던 퀴즈id를 알기 위한 것이었으므로, 생성 후엔 지워준다.)
  postMbtiHostQuizReport(body: QuizReportPostData): Observable<MyMbtiHostQuizReportDetailResponse> {
    return this.quizService.postHostQuizResult(body, QuizReportUrlType.MBTI).pipe(
      tap((res: MyMbtiHostQuizReportDetailResponse) => {
        this.userService.removeLatestAnsweredReport()
        if (res.data) {
          this.quizService.setQuizReportToLocalStorage('mbtiHostQuizReport', res.data)
          this.userService.getMbtiProperty()
          this.hostQuizReportCreated(res.data)
          this.myMbti = res.data.mbtiType
          this.logService.setUserConfig({ user_mbti_type: this.userMbti })
          if (this.userService.isLoggedIn) {
            this.userService.updateProfile({ mbtiQuizId: res.data.id }).subscribe()
          }
        }
      })
    )
  }

  // 게스트 응답 결과 저장
  postMbtiGuestQuizReport(body: GuestQuizReportData): Observable<MbtiGuestQuizReportDetailResponse> {
    return this.quizService.postGuestQuizResult(body, QuizReportUrlType.MBTI).pipe(
      tap((res: MbtiGuestQuizReportDetailResponse) => {
        if (body.hostQuizReport !== undefined) {
          this.quizService.onceAnsweredQuizList.push(body.hostQuizReport)
        }
        if (res.data) {
          this.guestQuizReportCreated(res.data)
          this.quizService.setQuizReportToLocalStorage('mbtiGuestQuizReport', res.data)
        }
      })
    )
  }

  /** 해당 quizReport의 계산 결과만 가져옴 */
  calculatedMbtiQuizResult(quizReportId: number): Observable<CalculatedResultResponse> {
    return this.http.get<CalculatedResultResponse>(`${quizApiUrl}/mbti/${quizReportId}/ratio`)
  }

  /** answers를 바탕으로 계산된 결과만 도출 - 서버에 저장 X */
  getCalculatedMbtiResult(answers: QuestionAnswer[]): Observable<TypeSummaryResponse> {
    return this.http.post<TypeSummaryResponse>(`${quizApiUrl}/mbti/test`, answers)
  }

  // 호스트 퀴즈 리포트 상세
  getMbtiQuizResult(quizReportId: number): Observable<MyMbtiHostQuizReportDetailResponse> {
    return this.http.get<MyMbtiHostQuizReportDetailResponse>(`${quizApiUrl}/mbti/${quizReportId}`)
  }

  // 게스트 퀴즈응답 상세
  getMbtiGuestQuizAnswerResult(guestQuizReportId: number): Observable<MbtiGuestQuizReportDetailResponse> {
    return this.http.get<MbtiGuestQuizReportDetailResponse>(`${quizApiUrl}/mbti/${guestQuizReportId}`)
  }

  /**
   * 퀴즈리포트 삭제
   * 대표퀴즈 상태 알림
   */
  removeMbtiQuizReport(quizReportId: number): Observable<void> {
    return this.http.delete(`${quizApiUrl}/mbti/${quizReportId}`, { loggingText: 'remove mbti quiz report' })
  }

  getMbtiGuestAnswerList(
    hostQuizReport: number,
    cursor: string | null,
    pageSize: number | null = null
  ): Observable<GuestAnswerPaginatedListResponse> {
    const queryParams = { cursor, pageSize, hostQuizReport }
    return this.http.get<GuestAnswerPaginatedListResponse>(`${quizApiUrl}/mbti`, { queryParams })
  }

  getMyMbtiHostQuizReportList(
    cursor: string | null,
    pageSize: number | null = null
  ): Observable<MyMbtiHostQuizReportPaginatedListResponse> {
    const queryParams = { cursor, pageSize, paginate: true }
    return this.http
      .get<MyMbtiHostQuizReportPaginatedListResponse>(`${quizApiUrl}/mbti/host/mine`, { queryParams })
      .pipe(
        tap((res) => {
          if (!this.myMbti && Array.isArray(res.data)) {
            const [firstData] = res.data
            this.myMbti = firstData?.mbtiType ?? null
            this.logService.setUserConfig({ user_mbti_type: this.myMbti })
          }
        })
      )
  }

  getMyMbtiGuestQuizReportList(
    cursor: string | null,
    pageSize: number | null = null
  ): Observable<MyMbtiGuestQuizReportPaginatedListResponse> {
    const queryParams = { cursor, pageSize }
    return this.http.get<MyMbtiGuestQuizReportPaginatedListResponse>(`${quizApiUrl}/mbti/guest/mine`, { queryParams })
  }

  // 해당 퀴즈에 내가 한 답변 중 최신 답변.
  getLatestAnswer(hostQuizReport: number): Observable<{ data: { guestQuizReportId: number } }> {
    const queryParams = { hostQuizReport }
    return this.http.get<{ data: { guestQuizReportId: number } }>(`${quizApiUrl}/mbti/guest/mine/latest`, {
      queryParams,
    })
  }

  getHostInformation(quizReportId: number): Observable<MbtiHostInfoResponse> {
    return this.quizService.getHostInformation(QuizReportUrlType.MBTI, quizReportId)
  }

  mapMbtiAnswerToScore(answer: number): number {
    return mbtiScores[answer]
  }

  mapMbtiScoreToAnswer(score: number): number {
    switch (score) {
      case -3:
        return 0
      case -2:
        return 1
      case -1:
        return 2
      case 1:
        return 3
      case 2:
        return 4
      case 3:
        return 5
      default:
        return -1
    }
  }

  // 만약 퀴즈 결과 데이터가 answer 형태일 경우, main 페이지로 리디렉트.
  // answer 형태일 경우 return
  redirectIfNotProperQuizResultResponse(res: MyMbtiHostQuizReportDetailResponse): boolean {
    if ((res.data as any)?.comparison !== undefined) {
      this.routerService.navigate('gnb/main')
      this.snackbarService.openSnackBar('잘못된 경로로 접근하셨습니다.')
      return true
    }
    return false
  }

  /**
   * MBTI 이상형 API
   */
  getMbtiIdealTypeDetail(quizId: number): Observable<MbtiIdealTypeQuizReportDetailResponse> {
    return this.http.get<MbtiIdealTypeQuizReportDetailResponse>(
      `${environment.apiUrl}/v3/quiz-reports/mbti/ideal-type/${quizId}`
    )
  }

  // quizFromId: MBTI 이상형 퀴즈 ID
  getMbtiIdealTypeMatchRanking(
    mbtiIdealTypeQuizId: number,
    cursor: string | null
  ): Observable<MbtiIdealTypeMatchPaginatedListResponse> {
    const queryParams = { mbtiIdealTypeQuizId, cursor, pageSize: 5 }
    return this.http.get<MbtiIdealTypeMatchPaginatedListResponse>(
      `${environment.apiUrl}/v3/quiz-reports/mbti/ideal-type/match/ranking`,
      { queryParams }
    )
  }

  /** MBTI 이상형 host info */
  getMbtiIdealTypeHostInfo(quizId: number): Observable<HostInfoResponse> {
    return this.http.get<HostInfoResponse>(`${environment.apiUrl}/v3/quiz-reports/mbti/ideal-type/${quizId}/info`)
  }

  createMbtiIdealTypeQuiz(body: QuizReportPostData): Observable<MbtiIdealTypeQuizReportDetailResponse> {
    return this.http.post<MbtiIdealTypeQuizReportDetailResponse>(
      `${environment.apiUrl}/v3/quiz-reports/mbti/ideal-type`,
      body
    )
  }
  createMbtiIdealTypeQuizByIdealTypeQuizId(idealTypeId: number): Observable<MbtiIdealTypeQuizReportDetailResponse> {
    const queryParams = { idealTypeId }
    return this.http.post<MbtiIdealTypeQuizReportDetailResponse>(
      `${environment.apiUrl}/v3/quiz-reports/mbti/ideal-type`,
      null,
      { queryParams }
    )
  }
  /** 이상형 궁합 host 계산만 하기 (비회원용) */
  calculateMbtiIdealTypeQuiz(body: QuizReportPostData): Observable<MbtiIdealTypeQuizReportDetailResponse> {
    return this.http.post<MbtiIdealTypeQuizReportDetailResponse>(
      `${environment.apiUrl}/v3/quiz-reports/mbti/ideal-type/test`,
      body
    )
  }

  /** 이상형 MBTI 퀴즈 매칭 생성 */
  postMbtiIdealTypeMatch(
    mbtiIdealTypeQuizId: number,
    mbtiQuizId: number,
    isProfileHidden: boolean,
    respondentFirstName: string
  ): Observable<MbtiIdealTypeQuizReportDetailResponse> {
    return this.http.post(`${environment.apiUrl}/v3/quiz-reports/mbti/ideal-type/match`, {
      mbtiIdealTypeQuizId,
      mbtiQuizId,
      isProfileHidden,
      respondentFirstName,
    })
  }

  getMbtiIdealTypeMatchDetail(matchId: number): Observable<MbtiIdealTypeMatchDetailResponse> {
    return this.http.get<MbtiIdealTypeMatchDetailResponse>(
      `${environment.apiUrl}/v3/quiz-reports/mbti/ideal-type/match/${matchId}`
    )
  }

  checkMbtiIdealTypeMatchDetail(
    mbtiIdealTypeQuizId: number,
    mbtiQuizId: number
  ): Observable<MbtiIdealTypeMatchCheckResponse> {
    const queryParams = { mbtiIdealTypeQuizId, mbtiQuizId }
    return this.http.get<MbtiIdealTypeMatchCheckResponse>(
      `${environment.apiUrl}/v3/quiz-reports/mbti/ideal-type/match/check`,
      { queryParams }
    )
  }

  /** 이상형 궁합 MBTI 변경 */
  changeMbtiIdealTypeHostMbti(
    idealTypeId: number,
    mbtiQuizId: number
  ): Observable<MbtiIdealTypeQuizReportDetailResponse> {
    const body = { myMbtiQuizId: mbtiQuizId }
    return this.http.patch<MbtiIdealTypeQuizReportDetailResponse>(
      `${environment.apiUrl}/v3/quiz-reports/mbti/ideal-type/${idealTypeId}`,
      body
    )
  }

  /**
   * MBTI 친친 API
   */
  getMbtiBestFriendDetail(quizId: number): Observable<MbtiBestFriendQuizReportDetailResponse> {
    return this.http.get<MbtiBestFriendQuizReportDetailResponse>(
      `${environment.apiUrl}/v3/quiz-reports/mbti/best-friend/${quizId}`
    )
  }

  // quizFromId: MBTI 친친 퀴즈 ID
  getMbtiBestFriendMatchRanking(
    mbtiBestFriendQuizId: number,
    cursor: string | null
  ): Observable<MbtiBestFriendMatchPaginatedListResponse> {
    const queryParams = { mbtiBestFriendQuizId, cursor, pageSize: 5 }
    return this.http.get<MbtiBestFriendMatchPaginatedListResponse>(
      `${environment.apiUrl}/v3/quiz-reports/mbti/best-friend/match/ranking`,
      { queryParams }
    )
  }

  /** MBTI 친친 host info */
  getMbtiBestFriendHostInfo(quizId: number): Observable<HostInfoResponse> {
    return this.http.get<HostInfoResponse>(`${environment.apiUrl}/v3/quiz-reports/mbti/best-friend/${quizId}/info`)
  }

  createMbtiBestFriendQuiz(body: QuizReportPostData): Observable<MbtiBestFriendQuizReportDetailResponse> {
    return this.http.post<MbtiBestFriendQuizReportDetailResponse>(
      `${environment.apiUrl}/v3/quiz-reports/mbti/best-friend`,
      body
    )
  }

  /** MBTI 친친 퀴즈 매칭 생성 */
  postMbtiBestFriendMatch(
    mbtiBestFriendQuizId: number,
    mbtiQuizId: number
  ): Observable<MbtiBestFriendQuizReportDetailResponse> {
    return this.http.post(`${environment.apiUrl}/v3/quiz-reports/mbti/best-friend/match`, {
      mbtiBestFriendQuizId,
      mbtiQuizId,
    })
  }

  getMbtiBestFriendMatchDetail(matchId: number): Observable<MbtiBestFriendMatchDetailResponse> {
    return this.http.get<MbtiBestFriendMatchDetailResponse>(
      `${environment.apiUrl}/v3/quiz-reports/mbti/best-friend/match/${matchId}`
    )
  }

  checkMbtiBestFriendMatchDetail(
    mbtiBestFriendQuizId: number,
    mbtiQuizId: number
  ): Observable<MbtiBestFriendMatchCheckResponse> {
    const queryParams = { mbtiBestFriendQuizId, mbtiQuizId }
    return this.http.get<MbtiBestFriendMatchCheckResponse>(
      `${environment.apiUrl}/v3/quiz-reports/mbti/best-friend/match/check`,
      { queryParams }
    )
  }

  /**
   * 이상형 MBTI Quiz
   */

  getIdealTypeQuizHostInfo(quizId: number): Observable<IdealTypeQuizReportDetailResponse> {
    return this.http.get<IdealTypeQuizReportDetailResponse>(
      `${environment.apiUrl}/v3/quiz-reports/ideal-type/${quizId}/host`
    )
  }

  // quizFromId: MBTI 이상형 퀴즈 ID
  getIdealTypeQuizRanking(
    idealTypeQuizId: number,
    cursor: string | null
  ): Observable<IdealTypeRankPaginatedListResponse> {
    const queryParams = { idealTypeQuizId, cursor, pageSize: 5 }
    return this.http.get<IdealTypeRankPaginatedListResponse>(
      `${environment.apiUrl}/v3/quiz-reports/ideal-type/${idealTypeQuizId}/host/ranking`,
      { queryParams }
    )
  }

  createIdealTypeQuiz(body: QuizReportPostData): Observable<IdealTypeQuizReportDetailResponse> {
    return this.http.post<IdealTypeQuizReportDetailResponse>(
      `${environment.apiUrl}/v3/quiz-reports/ideal-type/host`,
      body
    )
  }
  createIdealTypeQuizByMbtiIdealTypeId(mbtiIdealTypeId: number): Observable<IdealTypeQuizReportDetailResponse> {
    const queryParams = { mbtiIdealTypeId }
    return this.http.post<IdealTypeQuizReportDetailResponse>(
      `${environment.apiUrl}/v3/quiz-reports/ideal-type/host`,

      null,
      { queryParams }
    )
  }
  /** ideal quiz 계산만 하기 */
  calculateIdealTypeQuiz(body: QuizReportPostData): Observable<IdealTypeQuizReportDetailResponse> {
    return this.http.post<IdealTypeQuizReportDetailResponse>(
      `${environment.apiUrl}/v3/quiz-reports/ideal-type/test`,
      body
    )
  }

  createIdeaTypeReply(body: GuestQuizReportData): Observable<IdealTypeGuestQuizReportDetailResponse> {
    return this.http
      .post<IdealTypeGuestQuizReportDetailResponse>(`${environment.apiUrl}/v3/quiz-reports/ideal-type/guest`, body)
      .pipe(
        tap((res) => {
          this.quizService.setQuizReportToLocalStorage('idealTypeGuestQuizReport', res.data)
        })
      )
  }

  getIdeaTypeReply(quizId: number): Observable<IdealTypeGuestQuizReportDetailResponse> {
    return this.http.get<IdealTypeGuestQuizReportDetailResponse>(
      `${environment.apiUrl}/v3/quiz-reports/ideal-type/${quizId}/guest`
    )
  }

  /** 이상형 퀴즈 MBTI 변경 */
  changeIdealTypeHostMbti(
    idealTypeQuizId: number,
    mbtiQuizId: number
  ): Observable<MbtiIdealTypeQuizReportDetailResponse> {
    const body = { myMbtiQuizId: mbtiQuizId }
    return this.http.patch<MbtiIdealTypeQuizReportDetailResponse>(
      `${environment.apiUrl}/v3/quiz-reports/ideal-type/${idealTypeQuizId}/host`,
      body
    )
  }

  // 퀴즈
  idealQuizPlayCount(): Observable<{ data: { playCount: number } }> {
    return this.http.get<{ data: { playCount: number } }>(
      `${environment.apiUrl}/v2/question/report/generic/ideal-type/play-count`
    )
  }
  // 궁합
  idealMatchPlayCount(): Observable<{ data: { playCount: number } }> {
    return this.http.get<{ data: { playCount: number } }>(
      `${environment.apiUrl}/v2/question/report/generic/mbti-ideal-type/play-count`
    )
  }
}
